import { useState, useCallback } from 'react';
import { useToast } from '@/components/ui/use-toast';

// ✅ IMPORT FIREBASE SERVICES
import { db } from '@/lib/firebase';
import { collection, addDoc, serverTimestamp, doc, getDoc } from 'firebase/firestore';

import { debugLog } from '@/components/DebugConsole';

/**
 * Shared hook for song request functionality
 * Used by both Artist Mode and Karaoke Mode
 */
export const useSongRequests = (artistId, mode = 'artist', moderationEnabled = true, sessionId = null) => {
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const submitRequest = useCallback(async (requestData) => {
    if (!artistId) {
      toast({ title: "Error", description: "Artist information not found.", variant: "destructive" });
      return false;
    }

    setSubmitting(true);

    try {
      // Check if artist is accepting new requests
      // BUT allow requests that are already in basket (have pending payment)
      if (artistId) {
        const liveStatusRef = doc(db, 'live_status', artistId);
        const liveStatusSnap = await getDoc(liveStatusRef);
        if (liveStatusSnap.exists()) {
          const liveStatusData = liveStatusSnap.data();
          if (liveStatusData.is_live && liveStatusData.accepting_new_requests === false) {
            // Check if this request is already in basket (has pending payment)
            const hasPendingPayment = requestData.transaction_reference || requestData.payment_intent_id || 
              localStorage.getItem(`pending_payment_${artistId}_${requestData.song_id}`) ||
              sessionStorage.getItem(`pending_payment_${artistId}_${requestData.song_id}`);
            
            if (!hasPendingPayment) {
              toast({
                title: "Requests Paused",
                description: "This artist is not currently accepting new song requests. Please try again later.",
                variant: "destructive"
              });
              setSubmitting(false);
              return false;
            }
            // If has pending payment, allow through (in basket - revenue is good!)
          }
        }
      }

      // Prepare the request data (this logic is unchanged)
      const baseRequest = {
        artist_id: artistId,
        session_id: sessionId,
        requester_name: requestData.requester_name,
        note: requestData.note || '',
        status: moderationEnabled ? 'pending' : 'approved',
        created_at: serverTimestamp() // ✅ Use Firestore server timestamp
      };

      let finalRequest;
      if (mode === 'karaoke') {
        finalRequest = {
          ...baseRequest,
          song_title: requestData.song_title,
          original_artist: requestData.artist_name,
          amount_paid: 0,
          tip: 0,
          is_tip_only: false
        };
      } else {
        // Artist Mode - original_artist is optional (can be missing for artist's own songs)
        const tipAmount = parseFloat(requestData.tip) || 0;
        const songPrice = parseFloat(requestData.price) || 0;
        const totalPaid = songPrice + tipAmount;
        
        // Validate required fields for Artist Mode
        if (!requestData.song_id) {
          throw new Error('Song ID is required');
        }
        if (!requestData.song_title) {
          throw new Error('Song title is required');
        }
        
        // For Artist Mode, original_artist is optional - use fallback if missing
        // This allows artist's own songs to work without requiring original_artist field
        const originalArtist = requestData.original_artist || 'Unknown Artist';
        
        finalRequest = {
          ...baseRequest,
          song_id: requestData.song_id,
          song_title: requestData.song_title,
          original_artist: originalArtist, // Always set a value, even if it's a fallback
          requester_email: requestData.requester_email || '',
          amount_paid: totalPaid,
          tip: tipAmount,
          is_tip_only: false,
          // Trust Payments fields
          transaction_reference: requestData.transaction_reference || null,
          order_reference: requestData.order_reference || null,
          // Legacy Stripe fields (for backward compatibility)
          payment_intent_id: requestData.payment_intent_id || requestData.transaction_reference || null,
          payment_method_id: requestData.payment_method_id || null,
          payment_status: (requestData.transaction_reference || requestData.payment_intent_id) ? 'succeeded' : null,
        };
        
        // Ensure all numeric fields are valid numbers
        if (isNaN(finalRequest.amount_paid) || isNaN(finalRequest.tip)) {
          throw new Error(`Invalid payment amounts: amount_paid=${finalRequest.amount_paid}, tip=${finalRequest.tip}`);
        }
      }

      console.log(`🎤 Submitting ${mode} request to Firestore:`, finalRequest);
      debugLog('RECEIVE_REQUEST', `New ${mode} song request received`, { 
        artistId, 
        sessionId, 
        songTitle: finalRequest.song_title,
        requesterName: finalRequest.requester_name,
        amountPaid: finalRequest.amount_paid,
        mode
      });

      // ✅ REFACTORED: Use Firestore's addDoc to insert the new request
      const docRef = await addDoc(collection(db, 'requests'), finalRequest);

      console.log(`✅ ${mode} request submitted successfully to Firestore`);
      debugLog('SUCCESS', `Song request saved to Firestore successfully`, { 
        requestId: docRef.id, 
        artistId, 
        mode 
      });

      // Push to the artist is sent server-side (onSongRequestCreated) so paid flows and tips stay reliable.

      const successMessage = mode === 'karaoke' 
        ? `Your request for "${requestData.song_title}" has been sent!`
        : `Your request for "${requestData.song_title}" has been sent!`;

      toast({ title: "Request Sent!", description: successMessage });
      return true;

    } catch (error) {
      console.error(`❌ Exception submitting ${mode} request:`, error);
      console.error(`❌ Error code:`, error.code);
      console.error(`❌ Error message:`, error.message);
      console.error(`❌ Error stack:`, error.stack);
      console.error(`❌ Original requestData:`, requestData);
      
      // Log Firestore-specific errors
      if (error.code) {
        console.error(`❌ Firestore error code: ${error.code}`);
        if (error.code === 'permission-denied') {
          console.error(`❌ PERMISSION DENIED: Check Firestore rules for 'requests' collection`);
        } else if (error.code === 'invalid-argument') {
          console.error(`❌ INVALID ARGUMENT: Check data types and required fields`);
        }
      }
      
      // Show more specific error message
      let errorMessage = error.message || "An unexpected error occurred. Please try again.";
      if (error.code === 'permission-denied') {
        errorMessage = "Permission denied. Please ensure you're logged in and try again.";
      } else if (error.code === 'invalid-argument') {
        errorMessage = "Invalid data format. Please check your input and try again.";
      }
      
      toast({ 
        title: "Error Submitting Request", 
        description: errorMessage,
        variant: "destructive" 
      });
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [artistId, mode, sessionId, moderationEnabled, toast]);

  const submitTip = useCallback(async (tipData) => {
    if (!artistId) {
      toast({ title: "Error", description: "Artist information not found.", variant: "destructive" });
      return false;
    }

    setSubmitting(true);

    try {
      const tipRequest = {
        artist_id: artistId,
        session_id: sessionId,
        song_id: null,
        song_title: "Tip Jar",
        original_artist: "Tip",
        requester_name: tipData.requester_name,
        note: tipData.note || '',
        amount_paid: parseFloat(tipData.amount),
        tip: parseFloat(tipData.amount),
        is_tip_only: true,
        status: 'completed', // Tips are auto-completed
        payment_intent_id: tipData.payment_intent_id || null,
        payment_method_id: tipData.payment_method_id || null,
        payment_status: tipData.payment_intent_id ? 'succeeded' : null,
        created_at: serverTimestamp() // ✅ Use Firestore server timestamp
      };

      console.log(`💰 Submitting ${mode} tip to Firestore:`, tipRequest);

      // ✅ REFACTORED: Use Firestore's addDoc to insert the new tip
      const tipDocRef = await addDoc(collection(db, 'requests'), tipRequest);

      console.log(`✅ ${mode} tip submitted successfully to Firestore`);

      toast({ title: "Tip Sent!", description: `Thank you for your generosity!` });
      return true;

    } catch (error) {
      console.error(`❌ Exception submitting ${mode} tip:`, error);
      toast({ title: "Error", description: "An unexpected error occurred. Please try again.", variant: "destructive" });
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [artistId, mode, sessionId, toast]);

  return {
    submitting,
    submitRequest,
    submitTip
  };
};